import {getAvailableDocumentTypes} from '../../component/documentDownload/raiseDownloadRequest/raiseDownloadRequest';
import {getServiceRequestTypes} from '../../component/ServiceRequest/ServiceRequest';
import {useMenuVisibility} from '../../hooks/useMenuVisibility';
import {
  getDocumentRedirectionInfoAction,
  getServiceRequestInfoAction,
} from '../../redux/actions/Actions';
import {appStore} from '../../redux/createStore';
import {postAPICall} from '../../services/apiCallsHandler';
import {
  MENU_OPTIONS_UNLEASH_KEY,
  SERVICE_REQUEST_TYPE_BY_VIEW,
} from '../../utils/constant';
import {isServiceRequestAllowed} from '../../utils/roleAccess';
import Smartlook from '../../utils/smartlook';
import {Navigate} from '../../utils/util';
import sidebarJson from '../Sidebar/Sidebar.json';
import './GlobalSearch.css';

import React from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';

// Caps the staggered entrance delay so long result lists still appear quickly
const MAX_STAGGER_STEPS = 8;

// Mirrors the Sidebar and BurgerMenu rules so search only lists screens the user can open
export const getSearchableScreens = ({
  countryCode,
  noScheduleData,
  contractCounts,
  pastContractsCounts,
  contractNumberError,
  contracts,
  pastContracts,
  contractDetailsError,
  role,
  roleAccess,
  flag,
  isItemVisible,
}) => {
  const hasContractError = !!contractNumberError?.isError;
  const noContract = contractCounts <= 0 || hasContractError;
  const isAllowed = (item) => !flag || isItemVisible(item);
  const screens = [];

  if (!noScheduleData && isAllowed('home')) {
    screens.push({view: 'home', label: 'HOME'});
  }
  if (!noContract) {
    if (isAllowed('paymentSchedule')) {
      screens.push({view: 'paymentSchedule', label: 'PAYMENT_SCHEDULE'});
    }
    if (isAllowed('contractDetails')) {
      screens.push({
        view:
          contractCounts > 1 ? 'contractDetailsOverview' : 'contractDetails',
        label: 'CONTRACT_DETAILS',
      });
    }
    if (isAllowed('serviceRequest')) {
      screens.push({view: 'serviceRequest', label: 'SERVICE_REQUEST'});
      getServiceRequestTypes({
        country: countryCode,
        contracts,
        contractDetailsError,
        roleAccess,
      })
        .filter(
          (type) =>
            !type.hidden &&
            isServiceRequestAllowed(
              role,
              SERVICE_REQUEST_TYPE_BY_VIEW[type.value],
              roleAccess
            )
        )
        .forEach((type) =>
          screens.push({
            view: 'serviceRequest',
            label: type.label,
            parentLabel: 'SERVICE_REQUEST',
            serviceRequestType: type.value,
          })
        );
    }
  }
  if (!(noContract && pastContractsCounts <= 0) && isAllowed('download')) {
    screens.push({view: 'download', label: 'DOWNLOAD'});
    getAvailableDocumentTypes({
      country: countryCode,
      contracts,
      pastContracts,
    }).forEach((documentType) =>
      screens.push({
        view: 'download',
        label: documentType,
        parentLabel: 'DOWNLOAD',
        documentRedirect: {selectedDocType: documentType},
      })
    );
    screens.push({
      view: 'download',
      label: 'DOWNLOAD_CENTER',
      parentLabel: 'DOWNLOAD',
      documentRedirect: {selectedTab: 'downloadCenter'},
    });
  }
  (sidebarJson[countryCode]?.sidebar ?? []).forEach((item) => {
    if (
      isAllowed(item.item) &&
      !(item.isVisibleContractError && hasContractError)
    ) {
      screens.push({view: item.item, label: item.label});
    }
  });
  if (isAllowed('notification')) {
    screens.push({view: 'notification', label: 'NOTIFICATION'});
  }
  if (isAllowed('myAccount')) {
    screens.push({view: 'myAccount', label: 'MY_ACCOUNT'});
  }
  return screens;
};

// Ignores case and accents, so "pagina" finds "página"
const fold = (text) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// 0 means no match; every query word has to be somewhere in the label
const getMatchScore = (label, words) => {
  const folded = fold(label);
  if (!words.every((word) => folded.includes(word))) {
    return 0;
  }
  const phrase = words.join(' ');
  if (folded === phrase) {
    return 4;
  }
  if (folded.startsWith(phrase)) {
    return 3;
  }
  const labelWords = folded.split(/\s+/);
  const startsWords = words.every((word) =>
    labelWords.some((labelWord) => labelWord.startsWith(word))
  );
  return startsWords ? 2 : 1;
};

// Best match first; on equal scores a parent comes before its children
export const searchScreens = (screens, words, t) =>
  screens
    .map((screen) => ({screen, score: getMatchScore(t(screen.label), words)}))
    .filter(({score}) => score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        Boolean(a.screen.parentLabel) - Boolean(b.screen.parentLabel)
    )
    .map(({screen}) => screen);

const SearchIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="16.5" y1="16.5" x2="22" y2="22" />
  </svg>
);

const HighlightedText = ({text, words}) => {
  const folded = fold(text);
  const ranges = words
    .map((word) => [folded.indexOf(word), word.length])
    .filter(([start]) => start >= 0)
    .sort((a, b) => a[0] - b[0]);
  const parts = [];
  let cursor = 0;
  ranges.forEach(([start, length]) => {
    const from = Math.max(start, cursor);
    const end = start + length;
    if (end > from) {
      parts.push(text.slice(cursor, from));
      parts.push(
        <span key={from} className="global-search-match">
          {text.slice(from, end)}
        </span>
      );
      cursor = end;
    }
  });
  parts.push(text.slice(cursor));
  return <span>{parts}</span>;
};

const GlobalSearch = (props) => {
  const {t} = useTranslation();
  const {flag, isItemVisible} = useMenuVisibility(MENU_OPTIONS_UNLEASH_KEY);
  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const wrapperRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  const words = fold(query).split(/\s+/).filter(Boolean);
  const results = words.length
    ? searchScreens(
        getSearchableScreens({...props, flag, isItemVisible}),
        words,
        t
      )
    : [];
  // The results can shrink below the position the arrow keys had reached
  const active = Math.min(activeIndex, results.length - 1);

  const reset = () => {
    setQuery('');
    setActiveIndex(0);
    setIsOpen(false);
  };

  React.useEffect(() => {
    Smartlook.navigation('GlobalSearch');
  }, []);

  const selectScreen = (screen) => {
    // Not awaited so tracking never delays or blocks navigation
    postAPICall(
      'globalSearch',
      {
        countryCode: props.countryCode,
        searchKey: query.trim(),
        resultClicked: t(screen.label),
      },
      {errorTag: 'GLOBAL_SEARCH', redirectToOops: false}
    );
    if (screen.serviceRequestType) {
      appStore.dispatch(
        getServiceRequestInfoAction({
          selectedRequestType: screen.serviceRequestType,
        })
      );
    }
    if (screen.documentRedirect) {
      appStore.dispatch(
        getDocumentRedirectionInfoAction(screen.documentRedirect)
      );
    }
    Navigate(screen.view);
    reset();
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        reset();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  React.useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView?.({block: 'nearest'});
  }, [active]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (results.length > 0) {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((active + step + results.length) % results.length);
      }
    } else if (event.key === 'Enter' && results.length > 0) {
      selectScreen(results[active]);
    } else if (event.key === 'Escape') {
      reset();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`global-search ${isOpen ? 'open' : ''}`}
      data-testid="global-search"
    >
      <button
        type="button"
        className="global-search-toggle"
        aria-label={t('GLOBAL_SEARCH_PLACEHOLDER')}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <SearchIcon />
      </button>
      <div className="global-search-panel">
        <div className="global-search-box">
          <SearchIcon />
          <input
            ref={inputRef}
            type="search"
            className="global-search-input"
            placeholder={t('GLOBAL_SEARCH_PLACEHOLDER')}
            aria-label={t('GLOBAL_SEARCH_PLACEHOLDER')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
          />
        </div>
        {words.length ? (
          <ul ref={listRef} className="global-search-results">
            {results.length > 0 ? (
              results.map((screen, index) => (
                <li
                  key={`${screen.view}-${screen.label}`}
                  style={{'--i': Math.min(index, MAX_STAGGER_STEPS)}}
                >
                  <button
                    type="button"
                    // Enter opens the active result; the arrow keys and the mouse move it
                    className={`global-search-result${
                      index === active ? ' active' : ''
                    }`}
                    onMouseMove={() => setActiveIndex(index)}
                    onClick={() => selectScreen(screen)}
                  >
                    <HighlightedText text={t(screen.label)} words={words} />
                    {screen.parentLabel ? (
                      <span className="global-search-parent">
                        {t(screen.parentLabel)}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))
            ) : (
              <li className="global-search-empty">
                {t('GLOBAL_SEARCH_NO_RESULTS')}
              </li>
            )}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export const mapStateToProps = (state) => ({
  countryCode: state.appStates?.allContracts?.countryCode,
  noScheduleData: state.appStates?.noScheduleData,
  contractCounts: state.appStates?.contractNumbers?.contractCounts,
  pastContractsCounts: state.appStates?.contractNumbers?.pastContractsCounts,
  contractNumberError: state.appStates?.contractNumberError,
  contracts: state.appStates?.contractNumbers?.contracts,
  pastContracts: state.appStates?.contractNumbers?.pastContracts,
  contractDetailsError: state.appStates?.contractDetailsError,
  role: state.appStates?.role,
  roleAccess: state.appStates?.roleAccess,
});

export default connect(mapStateToProps)(GlobalSearch);
